import React, { useState, useEffect } from "react";
import axios from "axios";

const InstructorCourses = ({ platformUrl }) => {
    const [courses, setCourses] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        const uuid = params.get("uuid");

        if (!token || !uuid) {
            setError("Missing access token or UUID. Please log in.");
            return;
        }

        const fetchAllContentItems = async (courseId, token, parentId = "ROOT") => {
            const contents = [];
            const stack = [parentId];

            while (stack.length > 0) {
                const currentParentId = stack.pop();

                try {
                    const response = await axios.get(
                        `${platformUrl}/learn/api/public/v1/courses/${courseId}/contents?parentId=${currentParentId}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    for (const item of response.data.results) {
                        contents.push({
                            title: item.title,
                            type: item.contentHandler?.id || "unknown",
                        });

                        if (item.hasChildren) {
                            stack.push(item.id);
                        }
                    }
                } catch (err) {
                    console.warn(`Error fetching contents for course ${courseId}:`, err.message);
                }
            }

            return contents;
        };

        const fetchInstructorCourses = async () => {
            try {
                const userResponse = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/uuid:${uuid}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const userId = userResponse.data.id;

                const membershipsResponse = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/${userId}/courses`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const instructorMemberships = membershipsResponse.data.results.filter(
                    (membership) => membership.courseRoleId === "Instructor"
                );

                const courseDetails = await Promise.all(
                    instructorMemberships.map(async (membership) => {
                        const courseRes = await axios.get(
                            `${platformUrl}/learn/api/public/v3/courses/${membership.courseId}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        const contents = await fetchAllContentItems(membership.courseId, token);

                        return {
                            ...courseRes.data,
                            membershipRole: membership.courseRoleId,
                            contentItems: contents,
                        };
                    })
                );

                setCourses(courseDetails);
            } catch (err) {
                setError(err.message);
            }
        };

        fetchInstructorCourses();
    }, []);

    // 🔢 Build content type summary
    const contentTypeSet = new Set();
    const contentSummary = courses.map((course) => {
        const counts = {};
        for (const item of course.contentItems) {
            contentTypeSet.add(item.type);
            counts[item.type] = (counts[item.type] || 0) + 1;
        }
        return {
            courseId: course.courseId,
            counts,
        };
    });
    const sortedTypes = Array.from(contentTypeSet).sort();

    if (error) {
        return (
            <div className="bb-container bb-error">
                <h1>Error</h1>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="bb-container">
            <h1>Instructor Courses</h1>

            <table className="bb-table">
                <thead>
                <tr>
                    <th>Course ID</th>
                    <th>Name</th>
                    <th>Ultra</th>
                    <th>Availability</th>
                    <th>Content Items</th>
                </tr>
                </thead>
                <tbody>
                {courses.map((course) => (
                    <tr key={course.id}>
                        <td>{course.courseId}</td>
                        <td>{course.name}</td>
                        <td>{course.ultraStatus}</td>
                        <td>{course.availability?.available ? "Available" : "Unavailable"}</td>
                        <td>
                            <ul>
                                {course.contentItems.length > 0 ? (
                                    course.contentItems.map((item, index) => (
                                        <li key={index}>
                                            <strong>{item.title}</strong>{" "}
                                            <em>({item.type})</em>
                                        </li>
                                    ))
                                ) : (
                                    <li>No Content</li>
                                )}
                            </ul>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <h2>Content Type Summary</h2>
            <table className="bb-table">
                <thead>
                <tr>
                    <th>Course ID</th>
                    {sortedTypes.map((type) => (
                        <th key={type}>{type}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {contentSummary.map((summary) => (
                    <tr key={summary.courseId}>
                        <td>{summary.courseId}</td>
                        {sortedTypes.map((type) => (
                            <td key={type}>{summary.counts[type] || 0}</td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default InstructorCourses;
